import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Briefcase, Check, X, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const INTEGRATIONS = [
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Sync LeadXchange meetings to your Google Calendar (anonymous mode)',
    icon: Calendar,
    color: 'bg-blue-500',
    category: 'calendar',
  },
  {
    id: 'microsoft_calendar',
    name: 'Microsoft Calendar',
    description: 'Sync LeadXchange meetings to Outlook Calendar (anonymous mode)',
    icon: Calendar,
    color: 'bg-blue-600',
    category: 'calendar',
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync purchased leads to HubSpot CRM (anonymized contact data)',
    icon: Briefcase,
    color: 'bg-orange-500',
    category: 'crm',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Sync purchased leads to Salesforce CRM (anonymized contact data)',
    icon: Briefcase,
    color: 'bg-blue-700',
    category: 'crm',
    disabled: true,
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    description: 'Sync purchased leads to Pipedrive CRM (anonymized contact data)',
    icon: Briefcase,
    color: 'bg-green-600',
    category: 'crm',
    disabled: true,
  },
];

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState({});

  useEffect(() => {
    fetchConnectedIntegrations();

    // Check if returning from OAuth callback
    const integrationParam = searchParams.get('integration');
    const statusParam = searchParams.get('status');
    
    if (integrationParam && statusParam === 'connected') {
      const integration = INTEGRATIONS.find(i => i.id === integrationParam);
      toast.success(`${integration?.name || 'Integration'} connected successfully!`);
      // Clean URL
      window.history.replaceState({}, '', '/settings');
      fetchConnectedIntegrations();
    }
  }, [searchParams]);

  const fetchConnectedIntegrations = async () => {
    try {
      const res = await api.get('/integrations/list');
      setConnectedIntegrations(res.data.integrations || []);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    setConnecting({ ...connecting, [provider]: true });
    try {
      const res = await api.post(`/integrations/${provider}/connect`);
      const authUrl = res.data.authorization_url;
      
      if (!authUrl) {
        throw new Error('No authorization URL returned');
      }
      
      // Redirect to OAuth consent screen
      window.location.href = authUrl;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to connect ${provider}`);
      setConnecting({ ...connecting, [provider]: false });
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}? You can reconnect anytime.`)) {
      return;
    }
    
    try {
      await api.post(`/integrations/${provider}/disconnect`);
      toast.success(`${provider} disconnected`);
      fetchConnectedIntegrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const isConnected = (provider) => {
    return connectedIntegrations.some(i => i.provider === provider);
  };

  const getConnectionInfo = (provider) => {
    return connectedIntegrations.find(i => i.provider === provider);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">
            Settings
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Connect your calendar and CRM to streamline your LeadXchange workflow
          </p>
        </div>

        {/* Calendar Integrations */}
        <div className="mb-10">
          <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">
            Calendar Integration
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Sync LeadXchange meetings to your calendar. We use <strong>anonymous meeting bridge IDs</strong> — 
            counterparty emails are never exposed in your calendar.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {INTEGRATIONS.filter(i => i.category === 'calendar').map(integration => {
              const connected = isConnected(integration.id);
              const info = getConnectionInfo(integration.id);
              const isConnecting = connecting[integration.id];
              
              return (
                <Card key={integration.id} data-testid={`integration-card-${integration.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${integration.color} rounded-lg flex items-center justify-center`}>
                          <integration.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{integration.name}</CardTitle>
                          {connected && (
                            <Badge variant="outline" className="mt-1 text-xs border-green-200 bg-green-50 text-green-700">
                              <Check className="w-3 h-3 mr-1" />
                              Connected
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-xs mb-4">
                      {integration.description}
                    </CardDescription>
                    
                    {connected && info ? (
                      <div className="space-y-3">
                        {info.provider_email && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Account:</span> {info.provider_email}
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDisconnect(integration.id)}
                          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`disconnect-${integration.id}-btn`}
                        >
                          Disconnect
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleConnect(integration.id)}
                        disabled={integration.disabled || isConnecting}
                        className="w-full bg-[#0055FF] hover:bg-[#0044CC]"
                        data-testid={`connect-${integration.id}-btn`}
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Connect
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* CRM Integrations */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">
            CRM Integration
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Sync purchased leads to your CRM. All contact data is <strong>anonymized by default</strong> — 
            real emails/phones are never exposed unless you explicitly opt to de-anonymize.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {INTEGRATIONS.filter(i => i.category === 'crm').map(integration => {
              const connected = isConnected(integration.id);
              const info = getConnectionInfo(integration.id);
              const isConnecting = connecting[integration.id];
              
              return (
                <Card key={integration.id} data-testid={`integration-card-${integration.id}`} className={integration.disabled ? 'opacity-50' : ''}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${integration.color} rounded-lg flex items-center justify-center`}>
                          <integration.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            {integration.name}
                            {integration.disabled && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Coming Soon
                              </Badge>
                            )}
                          </CardTitle>
                          {connected && (
                            <Badge variant="outline" className="mt-1 text-xs border-green-200 bg-green-50 text-green-700">
                              <Check className="w-3 h-3 mr-1" />
                              Connected
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-xs mb-4">
                      {integration.description}
                    </CardDescription>
                    
                    {connected && info ? (
                      <div className="space-y-3">
                        {info.provider_account_id && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Account ID:</span> {info.provider_account_id}
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDisconnect(integration.id)}
                          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`disconnect-${integration.id}-btn`}
                        >
                          Disconnect
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleConnect(integration.id)}
                        disabled={integration.disabled || isConnecting}
                        className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50"
                        data-testid={`connect-${integration.id}-btn`}
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Connect
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-10 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-heading text-sm font-semibold text-gray-900 mb-2">
            Privacy & Security
          </h3>
          <ul className="text-xs text-gray-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>OAuth credentials are encrypted at rest and refreshed automatically</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Calendar events use generic meeting bridge IDs — counterparty emails never exposed</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>CRM contacts are anonymized by default (lead-xxx@internal.leadxchange.com placeholders)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Disconnect anytime — tokens are immediately revoked from LeadXchange</span>
            </li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
}
